import { contentLinkNodeNavigateTo } from 'src/modules/dashboard/helpers/contentLinkNodeNavigateTo';
import { ActionPropsWithId } from '@modernhealth/vital-components';
import i18n from 'src/config/i18n';
import { ContentLinkFragment } from '../documents/getHomeScreen.generated';
import { screenLinkToPath } from './contentLinkNodeToSectionHeader';
import { getFirstFacilitatorPhoto } from './contentLinkNodeToContentCard';
import { getTrackClickCallback } from './getTrackClickCallback';
import { ContentTracking } from '../components/ContentCard';

type MapContentLinkToActionItemProps = {
    link: ContentLinkFragment;
    showCirclesInMemberWeb: boolean;
    tracking: ContentTracking;
};

export const mapContentLinkToActionItemProps = ({
    link,
    showCirclesInMemberWeb = false,
    tracking,
}: MapContentLinkToActionItemProps): ActionPropsWithId | null => {
    switch (link.__typename) {
        case 'MeditationLinkNode':
            return link.meditation
                ? {
                      id: link.meditation.uuid,
                      title: link.meditation.name,
                      description: i18n.t(
                          link.mediaType?.name
                              ? `shared:media_type.duration_with_min`
                              : 'dashboard:meditation.duration',
                          {
                              duration: link.meditation.duration ?? 1,
                              mediaTypeName: link.mediaType?.name,
                          },
                      ),
                      startIconUrl: link.meditation.image,
                      trackClickCallback: getTrackClickCallback({ link, ...tracking }),
                      ...contentLinkNodeNavigateTo(link),
                  }
                : null;
        case 'ProgramLinkNode':
            return link.program
                ? {
                      id: link.program.uuid,
                      title: link.program.name,
                      description: i18n.t(
                          link.mediaType?.name
                              ? `shared:media_type.duration_with_min`
                              : 'dashboard:program.duration',
                          {
                              duration: link.program.duration ?? 1,
                              mediaTypeName: link.mediaType?.name,
                          },
                      ),
                      startIconUrl: link.program.image ?? '',
                      trackClickCallback: getTrackClickCallback({ link, ...tracking }),
                      ...contentLinkNodeNavigateTo(link),
                  }
                : null;
        case 'CircleSeriesLinkNode':
            return link.circleSeries
                ? {
                      id: link.circleSeries.id,
                      title: link.circleSeries.name,
                      description:
                          link.circleSeries.shortDescription || link.circleSeries.description,
                      startIconUrl: link.circleSeries.illustration ?? '',
                      trackClickCallback: getTrackClickCallback({ link, ...tracking }),
                      ...contentLinkNodeNavigateTo(link, showCirclesInMemberWeb),
                  }
                : null;
        case 'ExternalLinkNode':
            return {
                id: link.id,
                title: link.title,
                description: link.subtitle ?? undefined,

                startIconUrl: link.image ?? undefined,
                trackClickCallback: getTrackClickCallback({ link, ...tracking }),
                ...contentLinkNodeNavigateTo(link),
            };
        case 'InternalLinkNode':
            return {
                id: link.id,
                title: link.title,
                description: link.subtitle ?? undefined,
                startIconUrl: link.image ?? undefined,
                initials:
                    !link.image && link.providerInitials
                        ? link.providerInitials.replace(/\./g, '')
                        : undefined,
                trackClickCallback: getTrackClickCallback({ link, ...tracking }),
                ...contentLinkNodeNavigateTo(link),
            };
        case 'CircleTopicLinkNode':
            return link.circleTopic
                ? {
                      id: link.circleTopic.id,
                      title: link.circleTopic.name,
                      description: i18n.t(
                          `dashboard:circle.duration.${link.circleTopic.format.toLowerCase()}`,
                          { duration: link.circleTopic.duration },
                      ),
                      startIconUrl: getFirstFacilitatorPhoto(link),
                      trackClickCallback: getTrackClickCallback({ link, ...tracking }),
                      ...contentLinkNodeNavigateTo(link, showCirclesInMemberWeb),
                  }
                : null;
        case 'ScreenLinkNode':
            const path = screenLinkToPath(link.screen);

            return path
                ? {
                      id: link.id,
                      title: link.title,
                      description: link.subtitle ?? undefined,
                      startIconUrl: link.image ?? undefined,
                      linkTo: path,
                  }
                : null;
        case 'CourseLinkNode':
            return {
                id: link.id,
                title: link.course.title,
                description: i18n.t(
                    link.mediaType?.name
                        ? `shared:media_type.duration_with_min`
                        : 'courses:course.duration',
                    {
                        duration: link.course.duration ?? 1,
                        mediaTypeName: link.mediaType?.name,
                    },
                ),
                trackClickCallback: getTrackClickCallback({ link, ...tracking }),
                ...contentLinkNodeNavigateTo(link),
            };
        default:
            return null;
    }
};
